import { alpha, Box, styled, useTheme } from '@mui/material';
import dayjs from 'dayjs';
import { sortBy } from 'lodash';

import {
  EResourceType,
  ERouteLegResponseType,
  type LogisticsAircraftById,
  type LogisticsLocationsById,
  type RouteLegResponseType,
} from '#entities';
import { ECaseResourceType, ICONS } from '#shared';

import { getAbbreviation, getMarkerName, resolveIanaTzFromLeg } from '../lib';

import { FadingTextBox } from './FadingLocationLabel';

export const LegRow = styled(Box)(({ theme: { spacing } }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: spacing(0.5),
  flexWrap: 'nowrap',
  minWidth: 'max-content',
  height: '100%',
}));

export const TransportBox = styled(Box)(({ theme: { palette, spacing } }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: spacing(1),
  color: palette.common.white,
  whiteSpace: 'nowrap',
}));

export const LabelBox = styled(Box)(({ theme: { utils, palette } }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  fontWeight: 500,
  fontSize: utils.clamp(10, 18),
  height: utils.clamp(20, 38),
  backgroundColor: alpha(palette.common.white, 0.15),
  padding: '0 5px',
  letterSpacing: 0.5,
  whiteSpace: 'nowrap',
  minWidth: '90px',
}));

export const PathBox = styled(Box)(({ theme: { spacing, utils } }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: spacing(0.5),
  fontSize: utils.clamp(10, 18),
  letterSpacing: 0.5,
  whiteSpace: 'nowrap',
}));

export const ResourceBox = styled(Box)({
  display: 'inline-flex',
  alignItems: 'center',
  whiteSpace: 'nowrap',
});

export const ResourceBoxItem = styled(Box)(({ theme: { palette, utils } }) => ({
  color: palette.common.white,
  backgroundColor: palette.mapBlue[600],
  padding: '0 5px',
  height: utils.clamp(20, 38),
  letterSpacing: 0.5,
  display: 'inline-flex',
  alignItems: 'center',
  whiteSpace: 'nowrap',
}));

export const LocationTime = styled(Box)(
  ({ theme: { palette, utils, spacing } }) => ({
    display: 'inline-flex',
    alignItems: 'center',
    color: palette.common.white,
    backgroundColor: alpha(palette.common.white, 0.15),
    padding: '0 5px',
    height: utils.clamp(20, 38),
    letterSpacing: 0.5,
    gap: spacing(1),
    whiteSpace: 'nowrap',
  }),
);

const resourceSortingOrder = ['CS', 'S', 'D', ''];
const excludedFromRender = new Set([
  EResourceType.Aircraft,
  EResourceType.Crew,
]);

interface Props {
  leg: RouteLegResponseType;
  locationsById?: LogisticsLocationsById;
  aircraftById?: LogisticsAircraftById;
  isItalian?: boolean;
}

export const CaseLegItem = ({
  leg,
  locationsById,
  aircraftById,
  isItalian = false,
}: Props) => {
  const { utils } = useTheme();

  const aircraftRef = leg.resources.find(
    r => r.resourceType === EResourceType.Aircraft,
  );

  const registration =
    leg.$type === ERouteLegResponseType.TxCenterProviderAviaLeg ||
    leg.$type === ERouteLegResponseType.SupplementalLiftAviaLeg
      ? (leg.tailNumber ?? '----')
      : leg.$type === ERouteLegResponseType.CommercialFlightAviaLeg
        ? leg.flightNumber
        : ((aircraftRef
            ? aircraftById?.get(aircraftRef.resourceId)?.tailNumber
            : null) ?? '----');
  const iconSize = utils.clamp(22, 32);

  const isGround =
    leg.$type === ERouteLegResponseType.TmdxAffiliateGroundLeg ||
    leg.$type === ERouteLegResponseType.TxCenterProviderGroundLeg ||
    leg.$type === ERouteLegResponseType.CurriServiceGroundLeg ||
    leg.$type === ERouteLegResponseType.TmdxDriveAffiliateGroundLeg ||
    leg.$type === ERouteLegResponseType.TmdxDriveTedsGroundLeg ||
    leg.$type === ERouteLegResponseType.SelfServicedGroundLeg;
  const TransportIcon = isGround ? ICONS.groundOnly : ICONS.airMao;

  const startIana = resolveIanaTzFromLeg(leg, 'start', locationsById);
  const endIana = resolveIanaTzFromLeg(leg, 'end', locationsById);

  const depTime = dayjs('departureTime' in leg ? leg.departureTime : '')
    .tz(startIana)
    .format('HH:mm z');
  const arrTime = dayjs('arrivalTime' in leg ? leg.arrivalTime : '')
    .tz(endIana)
    .format('HH:mm z');

  const depLabel = getMarkerName(
    'departureLocation' in leg ? leg.departureLocation : null,
    isItalian,
    locationsById,
  );
  const arrLabel = getMarkerName(
    'arrivalLocation' in leg ? leg.arrivalLocation : null,
    isItalian,
    locationsById,
  );

  const resourcesWithoutAircraft = leg.resources.filter(
    r => !excludedFromRender.has(r.resourceType),
  );
  const resourceLabels: string[] = sortBy(
    resourcesWithoutAircraft.map(r =>
      getAbbreviation(r.resourceType as unknown as ECaseResourceType),
    ),
    label => resourceSortingOrder.indexOf(label),
  );

  return (
    <LegRow>
      <TransportBox>
        <LabelBox>
          <TransportIcon height={iconSize} width={iconSize} />
          {registration ? <span>{registration}</span> : null}
        </LabelBox>
      </TransportBox>

      <PathBox>
        <LocationTime>
          <FadingTextBox label={depLabel} width={100} />
          <span>{depTime}</span>
        </LocationTime>
        <LocationTime>
          <FadingTextBox label={arrLabel} width={100} />
          <span>{arrTime}</span>
        </LocationTime>

        {resourceLabels.length > 0 && (
          <ResourceBox>
            <ResourceBoxItem>{resourceLabels.join(', ')}</ResourceBoxItem>
          </ResourceBox>
        )}
      </PathBox>
    </LegRow>
  );
};
